const normalizeBaseUrl = (url) => String(url || '').trim().replace(/\/+$/, '');

const resolvePrimaryBackendUrl = () => {
  const envUrl = normalizeBaseUrl(process.env.REACT_APP_BACKEND_URL);
  if (envUrl) return envUrl;

  if (typeof window !== 'undefined') {
    const { origin, protocol, hostname } = window.location;
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';

    if (!isLocalHost && hostname) {
      return normalizeBaseUrl(origin);
    }

    if (isLocalHost) return `${protocol}//${hostname}:5000`;
  }

  return 'http://localhost:5000';
};

const BACKEND_URL = resolvePrimaryBackendUrl();

const getBackendCandidates = () => {
  const envUrl = normalizeBaseUrl(process.env.REACT_APP_BACKEND_URL);
  const candidates = [];

  if (typeof window !== 'undefined') {
    const { origin, protocol, hostname } = window.location;
    const normalizedOrigin = normalizeBaseUrl(origin);
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isVercelHost = hostname.endsWith('.vercel.app');

    if (normalizedOrigin) {
      candidates.push(normalizedOrigin);
    }

    if (hostname && (isLocalHost || (!envUrl && !isVercelHost))) {
      candidates.push(`${protocol}//${hostname}:5000`);
    }

    if (isLocalHost) {
      candidates.push('http://localhost:5000', 'http://127.0.0.1:5000');
    }
  }

  if (envUrl) candidates.push(envUrl);

  return [...new Set(candidates.map(normalizeBaseUrl).filter(Boolean))];
};

const withTimeout = async (url, options = {}, timeoutMs = 15000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });

    const contentType = response.headers.get('content-type') || '';
    const responseText = await response.text();
    let data = null;

    if (contentType.includes('application/json') && responseText) {
      data = JSON.parse(responseText);
    }

    return { response, data, contentType, responseText };
  } finally {
    clearTimeout(timer);
  }
};

const postJson = async (path, payload, timeoutMs = 20000) => {
  const candidates = getBackendCandidates();
  const timeoutPerCandidate = Math.max(6000, Math.floor(timeoutMs / Math.max(candidates.length, 1)));
  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify(payload || {})
  };
  let lastError;

  for (const baseUrl of candidates) {
    try {
      const result = await withTimeout(`${baseUrl}${path}`, requestOptions, timeoutPerCandidate);
      const isJsonResponse =
        result.contentType.includes('application/json') ||
        (result.data && typeof result.data === 'object');

      if (!isJsonResponse) {
        const error = new Error(`Non-JSON response from ${baseUrl}`);
        error.name = 'InvalidApiResponseError';
        lastError = error;
        continue;
      }

      return { ...result, backend_url: baseUrl };
    } catch (error) {
      lastError = error;
      const isRecoverableNetworkIssue =
        error?.name === 'AbortError' ||
        error instanceof TypeError ||
        error?.name === 'InvalidApiResponseError';

      if (!isRecoverableNetworkIssue) {
        throw error;
      }
    }
  }

  if (lastError) {
    lastError.backendCandidates = candidates;
    throw lastError;
  }

  const error = new Error('Failed to reach payment server');
  error.backendCandidates = candidates;
  throw error;
};

const stringifyError = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.message) return value.message;

  try {
    return JSON.stringify(value);
  } catch (_error) {
    return String(value);
  }
};

const buildBackendHint = (candidates = []) => {
  if (!Array.isArray(candidates) || !candidates.length) return BACKEND_URL;
  return candidates.join(', ');
};

export const generatePaymentUrls = (baseUrl = window.location.origin) => ({
  success_url: `${baseUrl}/payment/success`,
  failure_url: `${baseUrl}/payment/fail`,
  cancel_url: `${baseUrl}/payment/cancel`,
  webhook_url: `${BACKEND_URL}/api/payment/webhook`
});

export const processCheckout = async (checkoutData, preferences = {}) => {
  try {
    const paymentMethod =
      preferences.paymentMethod || checkoutData.paymentMethod || 'sslcommerz';

    const payload = {
      orderId: checkoutData.orderId || `ORDER_${Date.now()}`,
      amount: checkoutData.amount,
      currency: checkoutData.currency || 'BDT',
      customerInfo: checkoutData.customerInfo,
      paymentMethod,
      metadata: {
        orderReference: checkoutData.orderReference,
        source: 'web_checkout'
      }
    };

    const { response, data } = await postJson('/api/payment/initiate', payload, 25000);

    if (!response.ok || !data?.success) {
      const detailedMessage =
        stringifyError(data?.error) || data?.message || 'Failed to initiate payment';

      return {
        success: false,
        error: detailedMessage,
        message: detailedMessage
      };
    }

    // Cash-on-delivery flow does not need redirect.
    if (paymentMethod === 'cash') {
      return {
        success: true,
        isCash: true,
        orderId: payload.orderId,
        message: data.message || 'Cash on delivery order confirmed'
      };
    }

    return {
      success: true,
      checkout_url: data.checkout_url || data.paymentUrl || data.payment_url,
      payment_url: data.paymentUrl || data.payment_url || data.checkout_url,
      invoice_id: data.invoice_id || data.tran_id || payload.orderId,
      tran_id: data.tran_id || data.invoice_id || payload.orderId,
      session_key: data.session_key,
      message: data.message || 'Payment session created'
    };
  } catch (error) {
    if (error.name === 'AbortError') {
      const targets = buildBackendHint(error.backendCandidates);
      return {
        success: false,
        error: `Payment request timed out. Checked: ${targets}.`,
        message: `Payment request timed out. Checked: ${targets}.`
      };
    }

    if (error?.name === 'InvalidApiResponseError') {
      const targets = buildBackendHint(error.backendCandidates);
      return {
        success: false,
        error: `Payment API is not available at: ${targets}. Set REACT_APP_BACKEND_URL to your deployed backend.`,
        message: `Payment API is not available at: ${targets}. Set REACT_APP_BACKEND_URL to your deployed backend.`
      };
    }

    if (error instanceof TypeError) {
      const targets = buildBackendHint(error.backendCandidates);
      return {
        success: false,
        error: `Unable to reach payment server. Checked: ${targets}. Set REACT_APP_BACKEND_URL to a reachable backend URL.`,
        message: `Unable to reach payment server. Checked: ${targets}. Set REACT_APP_BACKEND_URL to a reachable backend URL.`
      };
    }

    return {
      success: false,
      error: error.message || 'Checkout failed',
      message: error.message || 'Checkout failed'
    };
  }
};

export const verifyPayment = async (paymentReference) => {
  try {
    let payload;

    if (typeof paymentReference === 'string') {
      payload = {
        invoice_id: paymentReference,
        tran_id: paymentReference
      };
    } else {
      payload = {
        val_id: paymentReference?.val_id,
        tran_id: paymentReference?.tran_id || paymentReference?.invoice_id,
        invoice_id: paymentReference?.invoice_id || paymentReference?.tran_id
      };
    }

    const { response, data } = await postJson('/api/payment/verify', payload);

    if (!response.ok || !data?.success) {
      const detailedMessage =
        stringifyError(data?.error) || data?.message || 'Payment verification failed';

      return {
        success: false,
        error: detailedMessage,
        data: null
      };
    }

    return {
      success: true,
      data: data.data
    };
  } catch (error) {
    if (error.name === 'AbortError') {
      return {
        success: false,
        error: 'Payment verification timed out. Please try again.',
        data: null
      };
    }

    return {
      success: false,
      error: error.message || 'Failed to verify payment',
      data: null
    };
  }
};

export const checkPaymentStatusWithRetry = async (
  paymentReference,
  maxRetries = 3,
  retryDelay = 4000
) => {
  let attempt = 0;

  while (attempt < maxRetries) {
    const result = await verifyPayment(paymentReference);

    if (!result.success) {
      if (attempt >= maxRetries - 1) {
        return result;
      }
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
      attempt += 1;
      continue;
    }

    if (!result.data || result.data.status !== 'PENDING' || attempt >= maxRetries - 1) {
      return result;
    }

    await new Promise((resolve) => setTimeout(resolve, retryDelay));
    attempt += 1;
  }

  return {
    success: false,
    error: 'Payment verification retries exceeded',
    data: null
  };
};

// Backward-compatible helpers
export const createPaymentCharge = processCheckout;
export const createSmartPayment = processCheckout;
export const preparePaymentData = (orderData, customerData, totalAmount, options = {}) => ({
  orderId: orderData?.id,
  amount: totalAmount,
  customerInfo: customerData,
  paymentMethod: options.paymentMethod || 'sslcommerz'
});
export const getApiVersionInfo = () => ({
  gateway: 'SSLCommerz',
  backend: BACKEND_URL
});

const paymentService = {
  BACKEND_URL,
  generatePaymentUrls,
  processCheckout,
  verifyPayment,
  checkPaymentStatusWithRetry,
  createPaymentCharge,
  createSmartPayment,
  preparePaymentData,
  getApiVersionInfo
};

export default paymentService;
